import api from '../client';

/**
 * Interface for compliance stat object with value and trend information
 */
export interface ComplianceStat {
  value: number;
  change: number;
  change_direction: 'positive' | 'negative';
}

/**
 * Interface for compliance stats response
 */
export interface ComplianceStatsResponse {
  success: boolean;
  data?: {
    total_payroll_deductions: ComplianceStat;
    employer_contributions: ComplianceStat;
    overall_compliance: ComplianceStat;
  };
  message?: string;
  error?: any;
}

/**
 * Interface for employer information
 */
export interface Employer {
  uuid: string;
  name: string;
}

/**
 * Interface for compliance record
 */
export interface ComplianceRecord {
  employer: Employer;
  payroll_deductions: number;
  employer_contributions: number;
  compliance_status: 'Compliant' | 'At Risk' | 'Non-Compliant';
  compliance_score: number;
}

/**
 * Interface for compliance records response
 */
export interface ComplianceRecordsResponse {
  success: boolean;
  data?: ComplianceRecord[];
  message?: string;
  error?: any;
}

/**
 * Interface for overall compliance response
 */
export interface OverallComplianceResponse {
  success: boolean;
  data?: {
    overall_compliance: number;
  };
  message?: string;
  error?: any;
}

/**
 * Interface for employer compliance response
 */
export interface EmployerComplianceResponse {
  success: boolean;
  data?: {
    employer_uuid: string;
    payroll_deductions: number;
    employer_contributions: number;
    compliance_score: number;
    compliance_status: 'Compliant' | 'At Risk' | 'Non-Compliant';
  };
  message?: string;
  error?: any;
}

/**
 * Interface for compliance trends response
 */
export interface ComplianceTrendsResponse {
  success: boolean;
  data?: {
    payroll_deduction_change: number;
    employer_contribution_change: number;
    compliance_change: number;
  };
  message?: string;
  error?: any;
}

/**
 * Interface for compliance filter parameters
 */
export interface ComplianceFilters {
  start_date?: string;
  end_date?: string;
  employer_uuid?: string;
  phrase?: string; // Added for search functionality
}

/**
 * ComplianceService class for interacting with the compliance API endpoints
 */
export class ComplianceService {
  private static baseUrl = '/admin/compliance';

  /**
   * Get compliance statistics including total payroll deductions, employer contributions, and overall compliance
   * @param filters Optional filters to apply (date range, employer)
   * @returns Promise with compliance statistics data
   */
  static async getComplianceStats(filters: ComplianceFilters = {}): Promise<ComplianceStatsResponse> {
    try {
      const response = await api.get<ComplianceStatsResponse>(`${this.baseUrl}/stats`, { params: filters });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to retrieve compliance statistics',
        error
      };
    }
  }

  /**
   * Get compliance records for all employers or a specific employer
   * @param filters Optional filters to apply (date range, employer)
   * @returns Promise with compliance records data
   */
  static async getComplianceRecords(filters: ComplianceFilters = {}): Promise<ComplianceRecordsResponse> {
    try {
      const response = await api.get<ComplianceRecordsResponse>(`${this.baseUrl}/records`, { params: filters });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to retrieve compliance records',
        error
      };
    }
  }

  /**
   * Get the overall system compliance percentage
   * @param filters Optional filters to apply (date range, employer)
   * @returns Promise with overall compliance data
   */
  static async getOverallCompliance(filters: ComplianceFilters = {}): Promise<OverallComplianceResponse> {
    try {
      const response = await api.get<OverallComplianceResponse>(`${this.baseUrl}/overall`, { params: filters });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to retrieve overall compliance',
        error
      };
    }
  }

  /**
   * Get compliance data for a specific employer
   * @param employerUuid UUID of the employer
   * @param filters Optional filters to apply (date range)
   * @returns Promise with employer compliance data
   */
  static async getEmployerCompliance(employerUuid: string, filters: Omit<ComplianceFilters, 'employer_uuid'> = {}): Promise<EmployerComplianceResponse> {
    try {
      const response = await api.get<EmployerComplianceResponse>(`${this.baseUrl}/employer/${employerUuid}`, { params: filters });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to retrieve employer compliance data',
        error
      };
    }
  }

  /**
   * Get compliance trends and percentage changes
   * @param filters Optional filters to apply (date range, employer)
   * @returns Promise with compliance trends data
   */
  static async getComplianceTrends(filters: ComplianceFilters = {}): Promise<ComplianceTrendsResponse> {
    try {
      const response = await api.get<ComplianceTrendsResponse>(`${this.baseUrl}/trends`, { params: filters });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to retrieve compliance trends',
        error
      };
    }
  }

  /**
   * Download compliance report as PDF (frontend-only implementation)
   * @param filters Optional filters to apply (date range, employer)
   * @param reportData Array of compliance records to include in the report
   * @param metrics Summary metrics for the report (totals and percentages)
   * @returns Promise that resolves when the download starts
   */
  static async downloadComplianceReportPDF(
    filters: ComplianceFilters = {}, 
    reportData: ComplianceRecord[] = [],
    metrics: { 
      totalPayrollDeductions: number,
      payrollGrowth: number,
      employerContributions: number,
      contributionsGrowth: number,
      overallCompliance: number,
      complianceGrowth: number
    } = {
      totalPayrollDeductions: 0,
      payrollGrowth: 0,
      employerContributions: 0,
      contributionsGrowth: 0,
      overallCompliance: 0,
      complianceGrowth: 0
    }
  ): Promise<void> {
    try {
      // Simulate API call with a delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Import jsPDF
      const { jsPDF } = await import('jspdf');
      
      // Create a new PDF document
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Use provided data or fallback to sample data if empty
      if (reportData.length === 0) {
        reportData = [
          {
            employer: { uuid: "1", name: "Tech Solutions Inc." },
            payroll_deductions: 45678.9,
            employer_contributions: 34567.89,
            compliance_status: "Compliant",
            compliance_score: 95
          },
          {
            employer: { uuid: "2", name: "Future Enterprises" },
            payroll_deductions: 32456.78,
            employer_contributions: 25678.9,
            compliance_status: "At Risk",
            compliance_score: 75
          },
          {
            employer: { uuid: "3", name: "Global Innovations" },
            payroll_deductions: 21345.67,
            employer_contributions: 15432.1,
            compliance_status: "Non-Compliant",
            compliance_score: 45
          }
        ];
      }
      
      // Define colors
      const primaryColor = [42, 157, 143]; // #2A9D8F
      const secondaryColor = [38, 70, 83]; // #264653
      const accentColor = [233, 196, 106]; // #E9C46A
      const lightGray = [244, 244, 244]; // #F4F4F4
      const darkGray = [51, 51, 51]; // #333333
      
      // Set font
      doc.setFont("helvetica");
      
      // Add header background
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, 210, 30, 'F');
      
      // Add logo/brand area
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.roundedRect(15, 10, 40, 15, 2, 2, 'FD');
      doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("LEAVE2GIVE", 35, 19, { align: 'center' });
      
      // Add title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.text('Payroll Compliance Report', 130, 19, { align: 'center' });
      
      // Add date and report info box
      doc.setFillColor(lightGray[0], lightGray[1], lightGray[2]);
      doc.roundedRect(15, 35, 180, 20, 3, 3, 'F');
      
      doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      
      // Add date
      doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 43);
      
      // Add filters if provided
      let filterText = '';
      if (filters.start_date && filters.end_date) {
        filterText = `Period: ${filters.start_date} to ${filters.end_date}`;
      } else if (filters.start_date) {
        filterText = `From: ${filters.start_date}`;
      } else if (filters.end_date) {
        filterText = `Until: ${filters.end_date}`;
      }
      
      if (filterText) {
        doc.text(filterText, 20, 50);
      }
      
      // Add metrics cards
      const cardWidth = 56;
      const cardSpacing = 6;
      let cardX = 15;
      const cardY = 60;
      const cardHeight = 35;
      
      // Card 1: Total Payroll Deductions
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(cardX, cardY, cardWidth, cardHeight, 3, 3, 'F');
      doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.setLineWidth(0.5);
      doc.roundedRect(cardX, cardY, cardWidth, cardHeight, 3, 3, 'S');
      
      // Card 1 content
      doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("Total Payroll Deductions", cardX + 5, cardY + 8);
      
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(this.formatCurrency(metrics.totalPayrollDeductions), cardX + 5, cardY + 20);
      
      // Growth indicator
      if (metrics.payrollGrowth !== 0) {
        const growthColor = metrics.payrollGrowth > 0 ? [16, 185, 129] : [239, 68, 68]; // #10b981 or #ef4444
        doc.setTextColor(growthColor[0], growthColor[1], growthColor[2]);
        doc.setFontSize(10);
        doc.text(formatGrowth(metrics.payrollGrowth), cardX + 5, cardY + 30);
      }
      
      // Card 2: Employer Contributions
      cardX += cardWidth + cardSpacing;
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(cardX, cardY, cardWidth, cardHeight, 3, 3, 'F');
      doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.roundedRect(cardX, cardY, cardWidth, cardHeight, 3, 3, 'S');
      
      // Card 2 content
      doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("Employer Contributions", cardX + 5, cardY + 8);
      
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(this.formatCurrency(metrics.employerContributions), cardX + 5, cardY + 20);
      
      // Growth indicator
      if (metrics.contributionsGrowth !== 0) {
        const growthColor = metrics.contributionsGrowth > 0 ? [16, 185, 129] : [239, 68, 68];
        doc.setTextColor(growthColor[0], growthColor[1], growthColor[2]);
        doc.setFontSize(10);
        doc.text(formatGrowth(metrics.contributionsGrowth), cardX + 5, cardY + 30);
      }
      
      // Card 3: Overall Compliance
      cardX += cardWidth + cardSpacing;
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(cardX, cardY, cardWidth, cardHeight, 3, 3, 'F');
      doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.roundedRect(cardX, cardY, cardWidth, cardHeight, 3, 3, 'S');
      
      // Card 3 content
      doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("Overall Compliance", cardX + 5, cardY + 8);
      
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(this.formatPercentage(metrics.overallCompliance), cardX + 5, cardY + 20);
      
      // Growth indicator
      if (metrics.complianceGrowth !== 0) {
        const growthColor = metrics.complianceGrowth > 0 ? [16, 185, 129] : [239, 68, 68];
        doc.setTextColor(growthColor[0], growthColor[1], growthColor[2]);
        doc.setFontSize(10);
        doc.text(formatGrowth(metrics.complianceGrowth), cardX + 5, cardY + 30);
      }
      
      // Helper function for growth formatting
      function formatGrowth(value: number): string {
        return value > 0 ? `+${value}%` : `${value}%`;
      }
      
      // Add table title
      doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Compliance Records", 15, 110);
      
      // Add table
      const tableTop = 115;
      const tableLeft = 15;
      const tableWidth = 180;
      const rowHeight = 10;
      const colWidths = [60, 40, 40, 25, 15];
      
      // Table header background
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(tableLeft, tableTop, tableWidth, rowHeight, 'F');
      
      // Table header text
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      
      const headers = ['Employer', 'Payroll Deductions', 'Contributions', 'Status', 'Score'];
      let colX = tableLeft + 3;
      
      headers.forEach((header, i) => {
        doc.text(header, colX, tableTop + 7);
        colX += colWidths[i];
      });
      
      // Table rows
      let rowY = tableTop + rowHeight;
      
      reportData.forEach((record, index) => {
        // Add new page if needed
        if (rowY > 270) {
          doc.addPage();
          
          // Add header to new page
          doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
          doc.rect(0, 0, 210, 15, 'F');
          
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont("helvetica", "bold");
          doc.text('Payroll Compliance Report - Continued', 105, 10, { align: 'center' });
          
          // Reset row position for new page
          rowY = 25;
          
          // Add table header on new page
          doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
          doc.rect(tableLeft, rowY, tableWidth, rowHeight, 'F');
          
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(10);
          
          colX = tableLeft + 3;
          headers.forEach((header, i) => {
            doc.text(header, colX, rowY + 7);
            colX += colWidths[i];
          });
          
          rowY += rowHeight;
        }
        
        // Row background (alternating)
        doc.setFillColor(index % 2 === 0 ? 255 : lightGray[0], lightGray[1], lightGray[2]);
        doc.rect(tableLeft, rowY, tableWidth, rowHeight, 'F');
        
        // Row data
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        
        colX = tableLeft + 3;
        
        // Employer name
        doc.text(record.employer.name, colX, rowY + 7);
        colX += colWidths[0];
        
        // Payroll deductions
        doc.text(this.formatCurrency(record.payroll_deductions), colX, rowY + 7);
        colX += colWidths[1];
        
        // Employer contributions
        doc.text(this.formatCurrency(record.employer_contributions), colX, rowY + 7);
        colX += colWidths[2];
        
        // Status with colored badge
        let statusColor;
        switch(record.compliance_status.toLowerCase()) {
          case 'compliant':
            statusColor = [6, 95, 70]; // #065f46
            break;
          case 'at risk':
            statusColor = [146, 64, 14]; // #92400e
            break;
          case 'non-compliant':
            statusColor = [185, 28, 28]; // #b91c1c
            break;
          default:
            statusColor = darkGray;
        }
        
        // Draw status badge
        doc.setFillColor(statusColor[0], statusColor[1], statusColor[2]);
        doc.roundedRect(colX, rowY + 2, 20, 6, 2, 2, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.text(record.compliance_status, colX + 10, rowY + 6, { align: 'center' });
        colX += colWidths[3];
        
        // Reset text color for score
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.setFontSize(9);
        doc.text(`${record.compliance_score}%`, colX, rowY + 7);
        
        rowY += rowHeight;
      });
      
      // Add footer
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        
        // Footer line
        doc.setDrawColor(lightGray[0], lightGray[1], lightGray[2]);
        doc.setLineWidth(0.5);
        doc.line(15, 280, 195, 280);
        
        // Footer text
        doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.text(`Generated on ${new Date().toLocaleString()}`, 15, 286);
        
        // Page number
        doc.text(`Page ${i} of ${pageCount}`, 195, 286, { align: 'right' });
      }
      
      // Save the PDF
      const filename = `compliance-report-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);
      
      return;
    } catch (error: any) {
      console.error('Error downloading PDF report:', error);
      throw new Error(error.message || 'Failed to download PDF report');
    }
  }

  /**
   * Download compliance report as CSV (frontend-only implementation)
   * @param filters Optional filters to apply (date range, employer)
   * @param reportData Array of compliance records to include in the report
   * @param metrics Summary metrics for the report (totals and percentages)
   * @returns Promise that resolves when the download starts
   */
  static async downloadComplianceReportCSV(
    filters: ComplianceFilters = {},
    reportData: ComplianceRecord[] = [],
    metrics: { 
      totalPayrollDeductions: number,
      payrollGrowth: number,
      employerContributions: number,
      contributionsGrowth: number,
      overallCompliance: number,
      complianceGrowth: number
    } = {
      totalPayrollDeductions: 0,
      payrollGrowth: 0,
      employerContributions: 0,
      contributionsGrowth: 0,
      overallCompliance: 0,
      complianceGrowth: 0
    }
  ): Promise<void> {
    try {
      // Simulate API call with a delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Use provided data or fallback to sample data if empty
      if (reportData.length === 0) {
        reportData = [
          {
            employer: { uuid: "1", name: "Tech Solutions Inc." },
            payroll_deductions: 45678.9,
            employer_contributions: 34567.89,
            compliance_status: "Compliant",
            compliance_score: 95
          },
          {
            employer: { uuid: "2", name: "Future Enterprises" },
            payroll_deductions: 32456.78,
            employer_contributions: 25678.9,
            compliance_status: "At Risk",
            compliance_score: 75
          },
          {
            employer: { uuid: "3", name: "Global Innovations" },
            payroll_deductions: 21345.67,
            employer_contributions: 15432.1,
            compliance_status: "Non-Compliant",
            compliance_score: 45
          }
        ];
      }
      
      // Generate CSV content
      let csvContent = "Payroll Compliance Report\n";
      csvContent += `Date,${new Date().toLocaleDateString()}\n`;
      
      // Add filters if provided
      if (filters.start_date && filters.end_date) {
        csvContent += `Period,${filters.start_date} to ${filters.end_date}\n`;
      } else if (filters.start_date) {
        csvContent += `From,${filters.start_date}\n`;
      } else if (filters.end_date) {
        csvContent += `Until,${filters.end_date}\n`;
      }
      
      // Add summary metrics
      csvContent += `\nSummary Metrics\n`;
      csvContent += `Total Payroll Deductions,${this.formatCurrency(metrics.totalPayrollDeductions)}\n`;
      csvContent += `Employer Contributions,${this.formatCurrency(metrics.employerContributions)}\n`;
      csvContent += `Overall Compliance,${this.formatPercentage(metrics.overallCompliance)}\n\n`;
      
      // Add table headers
      csvContent += "Employer,Payroll Deductions,Employer Contributions,Compliance Status,Compliance Score\n";
      
      // Add table data
      reportData.forEach(record => {
        csvContent += `"${record.employer.name}",`;
        csvContent += `${this.formatCurrency(record.payroll_deductions)},`;
        csvContent += `${this.formatCurrency(record.employer_contributions)},`;
        csvContent += `"${record.compliance_status}",`;
        csvContent += `${record.compliance_score}%\n`;
      });
      
      // Create a Blob and download it
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const filename = `compliance-report-${new Date().toISOString().split('T')[0]}.csv`;
      this.downloadFile(blob, filename, 'text/csv');
      
      return;
    } catch (error: any) {
      console.error('Error downloading CSV report:', error);
      throw new Error(error.message || 'Failed to download CSV report');
    }
  }

  /**
   * Utility method to format a compliance status with appropriate styling class
   * @param status Compliance status string
   * @returns CSS class name for styling
   */
  static getComplianceStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'compliant': return 'status-compliant';
      case 'at risk': return 'status-at-risk';
      case 'non-compliant': return 'status-non-compliant';
      default: return '';
    }
  }

  /**
   * Utility method to format currency values
   * @param amount Amount in dollars
   * @returns Formatted currency string
   */
  static formatCurrency(amount: number): string {
    if (amount === undefined || amount === null) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  /**
   * Utility method to format percentage values
   * @param value Percentage value
   * @returns Formatted percentage string
   */
  static formatPercentage(value: number): string {
    if (value === undefined || value === null) return '0%';
    return `${value.toFixed(1)}%`;
  }

  /**
   * Utility method to get an icon for trend direction
   * @param direction Trend direction ('positive' or 'negative')
   * @returns SVG icon string
   */
  static getTrendIcon(direction: 'positive' | 'negative'): string {
    if (direction === 'positive') {
      return `<svg class="trend-icon trend-up" viewBox="0 0 24 24" width="16" height="16">
        <path fill="currentColor" d="M7 14l5-5 5 5H7z"/>
      </svg>`;
    } else {
      return `<svg class="trend-icon trend-down" viewBox="0 0 24 24" width="16" height="16">
        <path fill="currentColor" d="M7 10l5 5 5-5H7z"/>
      </svg>`;
    }
  }

  /**
   * Helper method to trigger file download from blob data
   * @param data Blob data to download
   * @param filename Name to save the file as
   * @param mimeType MIME type of the file
   */
  private static downloadFile(data: Blob, filename: string, mimeType: string): void {
    // Create a blob URL for the data
    const blob = new Blob([data], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    
    // Create a temporary link element to trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    link.style.display = 'none';
    
    // Add to document, click to trigger download, then remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the blob URL
    window.URL.revokeObjectURL(url);
  }
} 